import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { Close, FileDownloadOutlined } from "@mui/icons-material";
import IGanhoFinanceiroRebalance from "@/interfaces/IGanhoFinanceiroRebalance";
import SimpleDataTable from "@/components/simpleDataTable";
import serviceRebalanceUsina from "@/services/serviceRebalanceUsina";
import { toast } from "react-toastify";

export interface IDialogGanhoFinanceiroProps {
    open: boolean;
    data: {
        usinaID: number;
        mes: string | number;
        ano: string | number;
    };
    onClose: () => void;
}

const numberFormatter = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function downloadCsv(data: IGanhoFinanceiroRebalance) {
    const header = ["Nº Instalação", "Cliente", "Cota Contratada", "Nova Cota", "Consumo Real", "Ganho (kWh)"];

    const rows = (data.instalacoes || []).map((item) => [
        item.numeroInstalacao,
        item.nomeCliente,
        numberFormatter.format(item.cotaContratada),
        numberFormatter.format(item.novaCota),
        numberFormatter.format(item.consumoReal),
        numberFormatter.format(item.ganhoKwh),
    ]);

    const csvContent = [header, ...rows]
        .map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(";"))
        .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const [mes, ano] = (data.cicloFaturamento || "").split("/");
    const nomeUsinaSanitizado = (data.nomeUsina || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .toUpperCase();

    link.href = url;
    link.download = `RESULTADO-FINANCEIRO-${nomeUsinaSanitizado}-${mes}-${ano}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}

export default function DialogGanhoFinanceiro({ open, data, onClose }: IDialogGanhoFinanceiroProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [warningMessage, setWarningMessage] = useState<string>("");
    const [ganhoData, setGanhoData] = useState<IGanhoFinanceiroRebalance | null>(null);

    const columns = useMemo(
        () => [
            {
                headerName: "Nº Instalação",
                field: "numeroInstalacao",
                type: "string",
                headerAlign: "left",
                width: 150,
            },
            {
                headerName: "Cliente",
                field: "nomeCliente",
                type: "string",
                headerAlign: "left",
                flex: 1,
                minWidth: 220,
            },
            {
                headerName: "Cota Contratada",
                field: "cotaContratada",
                type: "number",
                width: 160,
                valueFormatter: ({ value }: any) => numberFormatter.format(value ?? 0),
            },
            {
                headerName: "Nova Cota",
                field: "novaCota",
                type: "number",
                width: 140,
                valueFormatter: ({ value }: any) => numberFormatter.format(value ?? 0),
            },
            {
                headerName: "Consumo Real",
                field: "consumoReal",
                type: "number",
                width: 150,
                valueFormatter: ({ value }: any) => numberFormatter.format(value ?? 0),
            },
            {
                headerName: "Ganho (kWh)",
                field: "ganhoKwh",
                type: "number",
                width: 150,
                sortable: true,
                renderCell: (params: any) => (
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: params.value < 0 ? "error.main" : "success.main" }}
                    >
                        {numberFormatter.format(params.value ?? 0)}
                    </Typography>
                ),
            },
        ],
        []
    );

    const getGanhoFinanceiro = async () => {
        setIsLoading(true);
        setWarningMessage("");
        setGanhoData(null);

        serviceRebalanceUsina
            .getGanhoFinanceiro({ usinaID: data.usinaID, mes: data.mes, ano: data.ano })
            .then((result) => {
                if (!result) {
                    return;
                }

                if (result.status === 400) {
                    const errors = result.data?.errors;
                    setWarningMessage(
                        errors && errors.length > 0
                            ? errors.join(" ")
                            : "Não foi possível apurar o ganho financeiro para o ciclo informado."
                    );
                    return;
                }

                setGanhoData(result.data);
            })
            .catch(() => {
                toast.error("Ocorreu um erro ao obter o ganho financeiro do rebalance!");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (open) {
            getGanhoFinanceiro();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            open={open}
        >
            <DialogTitle>
                Ganho Financeiro do Rebalance
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box style={{ display: "flex", height: "120px", justifyContent: "center", alignItems: "center" }}>
                        <img alt="Loading" src="/Loading.gif" width="40px" height="40px" />
                    </Box>
                ) : warningMessage ? (
                    <Box style={{ display: "flex", height: "120px", justifyContent: "center", alignItems: "center" }}>
                        <Typography color="warning.main" align="center">
                            {warningMessage}
                        </Typography>
                    </Box>
                ) : ganhoData ? (
                    <Box>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Usina
                                </Typography>
                                <Typography variant="subtitle1">{ganhoData.nomeUsina}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Ciclo
                                </Typography>
                                <Typography variant="subtitle1">{ganhoData.cicloFaturamento}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Status do Rebalance
                                </Typography>
                                <Typography variant="subtitle1">{ganhoData.status}</Typography>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Ganho Total (kWh)
                                    </Typography>
                                    <Typography variant="h5">
                                        {numberFormatter.format(ganhoData.ganhoTotalKwh)}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: "center", borderColor: "success.main" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Qtd. instalações com ganho
                                    </Typography>
                                    <Typography variant="h5" color="success.main">
                                        {ganhoData.quantidadeInstalacoesComGanho}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: "center", borderColor: "error.main" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Qtd. instalações com redução
                                    </Typography>
                                    <Typography variant="h5" color="error.main">
                                        {ganhoData.quantidadeInstalacoesComReducao}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Box sx={{ height: 420 }}>
                            <SimpleDataTable
                                columns={columns}
                                rows={ganhoData.instalacoes || []}
                                rowId={(row: any) => row.numeroInstalacao}
                            />
                        </Box>
                    </Box>
                ) : null}
            </DialogContent>
            <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                {ganhoData && ganhoData.instalacoes && ganhoData.instalacoes.length > 0 && (
                    <Button
                        onClick={() => downloadCsv(ganhoData)}
                        color="primary"
                        variant="contained"
                        startIcon={<FileDownloadOutlined />}
                        style={{ height: "44px" }}
                    >
                        Exportar CSV
                    </Button>
                )}
                <Button onClick={onClose} color="warning" variant="contained" style={{ width: "159px", height: "44px" }}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
